import { useLazyQuery } from '@apollo/client'
import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { Modal } from '@components/Modal'
import { useContentReportCreate } from '@services/api/graphql/mutations/contentReportCreate'
import {
  IContentReportData,
  GET_CONTENT_REPORT,
} from '@services/api/graphql/queries/getContentReport'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, Pressable } from 'react-native'
import { Text, XStack, YStack } from 'tamagui'

type ModalState = 'select' | 'success' | 'already_reported' | 'error'

const REPORT_REASONS = [
  'INAPPROPRIATE_FOR_MINORS',
  'PREDATORY_PRACTICES',
  'HARASSMENT_OR_MISCONDUCT',
  'COPYRIGHT_VIOLATION',
] as const

interface IContentReportModalProps {
  open: boolean
  onClose: () => void
  schoolProductId: string
  courseId: string
  lessonId?: string
  courseContentId?: string
}

export function ContentReportModal({
  open,
  onClose,
  schoolProductId,
  courseId,
  lessonId,
  courseContentId,
}: IContentReportModalProps) {
  const { t } = useTranslation('ContentReport')
  const { handleCreateReport, loading: submitting } = useContentReportCreate()
  const [fetchReport, { loading: checking }] = useLazyQuery<IContentReportData>(
    GET_CONTENT_REPORT,
    {
      fetchPolicy: 'network-only',
    },
  )
  const [modalState, setModalState] = useState<ModalState>('select')
  const [selectedReason, setSelectedReason] = useState<
    (typeof REPORT_REASONS)[number] | null
  >(null)

  useEffect(() => {
    if (!open) return
    fetchReport({ variables: { courseId } }).then(({ data }) => {
      if (data?.contentReport) {
        setModalState('already_reported')
      }
    })
  }, [open, courseId, fetchReport])

  const handleClose = useCallback(() => {
    setModalState('select')
    setSelectedReason(null)
    onClose()
  }, [onClose])

  const handleSubmit = useCallback(async () => {
    if (!selectedReason) return

    try {
      const result = await handleCreateReport({
        schoolProductId,
        courseId,
        lessonId,
        courseContentId,
        reason: selectedReason,
      })

      if (result?.status === 'already_reported') {
        setModalState('already_reported')
      } else {
        setModalState('success')
      }
    } catch {
      setModalState('error')
    }
  }, [
    selectedReason,
    handleCreateReport,
    schoolProductId,
    courseId,
    lessonId,
    courseContentId,
  ])

  return (
    <Modal
      open={open}
      onClose={handleClose}
      maxWidth={480}
      snapPointsMode="fit"
    >
      <YStack p="$spacing-inset-md" gap="$spacing-inset-sm">
        {checking && (
          <YStack alignItems="center" py="$spacing-inset-lg">
            <ActivityIndicator size="large" />
          </YStack>
        )}

        {!checking && modalState === 'select' && (
          <>
            <YStack gap="$spacing-inset-xs">
              <XStack alignItems="center" gap="$spacing-inset-xs">
                <HeroIcon
                  icon={['fal', 'megaphone']}
                  size={20}
                  color="$danger-300"
                />
                <Text
                  fontSize="$font-size-md"
                  fontFamily="$mulishBold"
                  color="$text-primary"
                >
                  {t('modal.title')}
                </Text>
              </XStack>
              <Text
                fontSize="$font-size-xxs"
                fontFamily="$mulishRegular"
                color="$text-secondary"
              >
                {t('modal.description')}
              </Text>
            </YStack>

            <YStack gap="$spacing-inset-xs">
              {REPORT_REASONS.map((reason) => (
                <Pressable
                  key={reason}
                  testID={`report-reason-${reason}`}
                  onPress={() => setSelectedReason(reason)}
                >
                  <XStack
                    alignItems="center"
                    gap="$spacing-inset-xs"
                    p="$spacing-inset-xs"
                    borderRadius="$border-radius-sm"
                    borderWidth={1}
                    borderColor={
                      selectedReason === reason ? '$accent' : '$border-default'
                    }
                    backgroundColor={
                      selectedReason === reason
                        ? '$bg-accent-soft'
                        : '$bg-surface'
                    }
                  >
                    <XStack
                      width={20}
                      height={20}
                      borderRadius={10}
                      borderWidth={2}
                      borderColor={
                        selectedReason === reason
                          ? '$accent'
                          : '$control-border'
                      }
                      alignItems="center"
                      justifyContent="center"
                    >
                      {selectedReason === reason && (
                        <XStack
                          width={10}
                          height={10}
                          borderRadius={5}
                          backgroundColor="$accent"
                        />
                      )}
                    </XStack>
                    <YStack flex={1} gap="$spacing-inset-quarck">
                      <Text
                        fontSize="$font-size-xxs"
                        fontFamily={
                          selectedReason === reason
                            ? '$mulishBold'
                            : '$mulishRegular'
                        }
                        color={
                          selectedReason === reason
                            ? '$neutral-900'
                            : '$text-primary'
                        }
                      >
                        {t(`reasons.${reason}`)}
                      </Text>
                      <Text
                        fontSize="$font-size-xxxs"
                        fontFamily="$mulishRegular"
                        color="$text-muted"
                      >
                        {t(`reasonsDescription.${reason}`)}
                      </Text>
                    </YStack>
                  </XStack>
                </Pressable>
              ))}
            </YStack>

            <Button
              variant="danger"
              onPress={handleSubmit}
              disabled={!selectedReason || submitting}
              loading={submitting}
            >
              {t('modal.submit')}
            </Button>
          </>
        )}

        {modalState === 'success' && (
          <YStack
            alignItems="center"
            gap="$spacing-inset-sm"
            py="$spacing-inset-md"
          >
            <HeroIcon
              icon={['fas', 'check-circle']}
              size={48}
              color="$success-300"
            />
            <Text
              fontSize="$font-size-md"
              fontFamily="$mulishBold"
              color="$text-primary"
              textAlign="center"
            >
              {t('modal.successTitle')}
            </Text>
            <Text
              fontSize="$font-size-xxs"
              fontFamily="$mulishRegular"
              color="$text-secondary"
              textAlign="center"
            >
              {t('modal.successDescription')}
            </Text>
            <Button variant="primary" onPress={handleClose}>
              {t('modal.close')}
            </Button>
          </YStack>
        )}

        {modalState === 'already_reported' && (
          <YStack
            alignItems="center"
            gap="$spacing-inset-sm"
            py="$spacing-inset-md"
          >
            <HeroIcon
              icon={['fas', 'info-circle']}
              size={48}
              color="$info-300"
            />
            <Text
              fontSize="$font-size-md"
              fontFamily="$mulishBold"
              color="$text-primary"
              textAlign="center"
            >
              {t('modal.alreadyReportedTitle')}
            </Text>
            <Text
              fontSize="$font-size-xxs"
              fontFamily="$mulishRegular"
              color="$text-secondary"
              textAlign="center"
            >
              {t('modal.alreadyReportedDescription')}
            </Text>
            <Button variant="primary" onPress={handleClose}>
              {t('modal.close')}
            </Button>
          </YStack>
        )}

        {modalState === 'error' && (
          <YStack
            alignItems="center"
            gap="$spacing-inset-sm"
            py="$spacing-inset-md"
          >
            <HeroIcon
              icon={['fas', 'exclamation-circle']}
              size={48}
              color="$danger-300"
            />
            <Text
              fontSize="$font-size-md"
              fontFamily="$mulishBold"
              color="$text-primary"
              textAlign="center"
            >
              {t('modal.errorTitle')}
            </Text>
            <Text
              fontSize="$font-size-xxs"
              fontFamily="$mulishRegular"
              color="$text-secondary"
              textAlign="center"
            >
              {t('modal.errorDescription')}
            </Text>
            <Button variant="primary" onPress={handleClose}>
              {t('modal.close')}
            </Button>
          </YStack>
        )}
      </YStack>
    </Modal>
  )
}
